import * as React from 'react';
import { DayButton, type DayButtonProps, DayPicker } from 'react-day-picker';
import { Button } from '@zep-us/zus';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import 'react-day-picker/style.css';
import { ChevronLeftIcon, ChevronRightIcon } from '@/icons';
import { cn } from '@/utils/tailwind.util';
export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  /**
   * 고를 수 없는 날짜를 눌렀을 때 호출된다.
   *
   * react-day-picker 는 disabled 날짜에 `disabled` 속성을 걸어 클릭 자체를
   * 삼키므로, 기본 상태로는 "왜 못 고르는지" 알려줄 기회가 없다. 이 콜백을
   * 넘기면 disabled 날짜만 `aria-disabled` 버튼으로 바꿔 클릭을 받는다.
   * 흐림 처리는 셀(td)의 modifier 클래스가 담당하므로 그대로 유지된다.
   *
   * 이유를 안내해야 하는 화면에서만 넘긴다. 넘기지 않으면 기존 동작 그대로다.
   */
  onDisabledDayClick?: (date: Date) => void;
};
const CalendarChevron = (params: {
  orientation?: 'up' | 'down' | 'left' | 'right';
}) => params.orientation === 'left' ? <ChevronLeftIcon size={'xs'} fill={ICON_COLOR.neutral} /> : <ChevronRightIcon size={'xs'} fill={ICON_COLOR.neutral} />;
const CalendarMonthButton = (params: React.ComponentProps<typeof Button>) => <Button intent={'neutral'} variant={'transparent'} size={'xs'} {...params} data-sentry-element="Button" data-sentry-component="CalendarMonthButton" data-sentry-source-file="calendar.tsx" />;
const CALENDAR_COMPONENTS = {
  Chevron: CalendarChevron,
  NextMonthButton: CalendarMonthButton,
  PreviousMonthButton: CalendarMonthButton
};

/**
 * DayPicker 의 `components` 는 렌더마다 새 컴포넌트를 넘기면 셀이 통째로
 * 리마운트된다. 그래서 콜백은 컴포넌트 안에서 클로저로 잡지 않고 context 로
 * 내려보내고, DayButton 대체 컴포넌트는 모듈 최상단에 한 번만 정의한다.
 */
const DisabledDayClickContext = React.createContext<((date: Date) => void) | null>(null);
function InterceptingDayButton({
  day,
  modifiers,
  disabled,
  onClick,
  ...buttonProps
}: DayButtonProps) {
  const onDisabledDayClick = React.useContext(DisabledDayClickContext);
  if (modifiers.disabled && onDisabledDayClick) {
    return <button {...buttonProps} type="button" aria-disabled onClick={() => onDisabledDayClick(day.date)} />;
  }
  return <DayButton {...buttonProps} day={day} modifiers={modifiers} disabled={disabled} onClick={onClick} data-sentry-element="DayButton" data-sentry-component="InterceptingDayButton" data-sentry-source-file="calendar.tsx" />;
}
const CALENDAR_COMPONENTS_WITH_DISABLED_CLICK = {
  ...CALENDAR_COMPONENTS,
  DayButton: InterceptingDayButton
};
export function Calendar({
  className,
  classNames,
  navLayout,
  locale,
  onDisabledDayClick,
  ...props
}: CalendarProps) {
  const components = onDisabledDayClick ? CALENDAR_COMPONENTS_WITH_DISABLED_CLICK : CALENDAR_COMPONENTS;
  return <DisabledDayClickContext.Provider value={onDisabledDayClick ?? null} data-sentry-element="unknown" data-sentry-component="Calendar" data-sentry-source-file="calendar.tsx">
      <DayPicker className={cn('p-md min-w-[328px] flex', className)} locale={locale} components={components} classNames={{
      months: 'w-full',
      weekday: 'size-[36px] text-body-md-semibold text-default',
      month_caption: 'flex h-[42px] items-center',
      caption_label: 'text-body-lg-semibold text-default',
      month_grid: 'w-full border-spacing-none mt-[10px]',
      day: cn('size-[36px] p-none text-body-md-semibold rounded-full hover:bg-[rgba(28,_27,_31,_0.04)] data-disabled:hover:bg-transparent text-default ' + 'aria-selected:!text-inverse aria-selected:hover:bg-primary'),
      day_range_end: 'day-range-end',
      selected: 'bg-strong rounded-full text-inverse',
      day_selected: 'bg-primary hover:bg-strong !text-inverse rounded-full',
      day_today: 'text-strong aria-selected:!text-inverse aria-selected:bg-primary',
      day_outside: 'text-disabled ',
      day_disabled: 'text-disabled ',
      // day_button: 'text-inverse',
      day_range_middle: 'aria-selected:bg-slate-100 aria-selected:text-slate-900',
      day_hidden: 'invisible',
      ...classNames
    }} {...props} data-sentry-element="DayPicker" data-sentry-source-file="calendar.tsx" />
    </DisabledDayClickContext.Provider>;
}
Calendar.displayName = 'Calendar';